import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  HStack,
  Select,
  FormErrorMessage,
  useToast,
  Badge,
  Spinner,
  Text,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import LineItemsTable from "./LineItemsTable";
import WysiwygEditor from "../../../components/WysiwygEditor";
import procurementApi from "../services/procurementApi";
import organizationalStructureApi from "../services/organizationalStructureApi";

const RequestForm = ({
  initialData = null,
  onSubmit,
  onCancel,
  isLoading,
  workflowOptions = [],
  showWorkflowSelect = false,
}) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    unit: "",
    department: "",
    branch: "",
    category: "",
    priority: "MEDIUM",
    estimatedBudget: "",
    justification: "",
    expectedDeliveryDate: "",
  });

  const [lineItems, setLineItems] = useState([
    {
      id: Date.now(),
      itemName: "",
      description: "",
      quantity: 1,
      unitPrice: 0,
      unit: "pcs",
      totalPrice: 0,
    },
  ]);

  const [errors, setErrors] = useState({});

  const formatDateForInput = (value) => {
    if (!value) {
      return "";
    }

    if (typeof value === "string") {
      const datePrefixMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
      if (datePrefixMatch) {
        return datePrefixMatch[0];
      }
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const normalizeLineItems = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) {
      return [
        {
          id: Date.now(),
          itemName: "",
          description: "",
          quantity: 1,
          unitPrice: 0,
          unit: "pcs",
          totalPrice: 0,
        },
      ];
    }

    return items.map((item, index) => {
      const quantity = Number(item?.quantity) || 1;
      const unitPrice = Number(item?.unitPrice) || 0;
      const computedTotal = quantity * unitPrice;

      return {
        ...item,
        id: item?.id || item?._id || Date.now() + index,
        itemName: item?.itemName || "",
        description: item?.description || "",
        unit: item?.unit || "pcs",
        quantity,
        unitPrice,
        totalPrice:
          item?.totalPrice !== undefined && item?.totalPrice !== null
            ? Number(item.totalPrice) || computedTotal
            : computedTotal,
      };
    });
  };

  // Business unit options from backend
  const [businessUnits, setBusinessUnits] = useState({
    units: [],
    departments: [],
    branches: [],
    priorities: [],
    categories: [],
  });

  // Resolved workflow state
  const [resolvedWorkflow, setResolvedWorkflow] = useState(null);
  const [resolvedWorkflows, setResolvedWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState("");
  const [workflowLoading, setWorkflowLoading] = useState(false);

  // Fetch business units on component mount
  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const [
          unitsResult,
          departmentsResult,
          branchesResult,
          categoriesResult,
        ] = await Promise.allSettled([
          organizationalStructureApi.getUnits({ isActive: true }),
          organizationalStructureApi.getDepartments({ isActive: true }),
          organizationalStructureApi.getBranches({ isActive: true }),
          organizationalStructureApi.getCategories({ isActive: true }),
        ]);

        const units =
          unitsResult.status === "fulfilled" ? unitsResult.value : [];
        const departments =
          departmentsResult.status === "fulfilled"
            ? departmentsResult.value
            : [];
        const branches =
          branchesResult.status === "fulfilled" ? branchesResult.value : [];
        const categories =
          categoriesResult.status === "fulfilled" ? categoriesResult.value : [];

        setBusinessUnits({
          units: (units || []).filter((item) => item.code),
          departments: (departments || []).filter((item) => item.code),
          branches: (branches || []).filter((item) => item.code),
          priorities: ["LOW", "MEDIUM", "HIGH", "URGENT"],
          categories: (categories || [])
            .map((item) => item.name)
            .filter(Boolean),
        });

        const failedSources = [
          unitsResult.status === "rejected" ? "Units" : null,
          departmentsResult.status === "rejected" ? "Departments" : null,
          branchesResult.status === "rejected" ? "Branches" : null,
          categoriesResult.status === "rejected" ? "Categories" : null,
        ].filter(Boolean);

        if (failedSources.length > 0) {
          toast({
            title: "Some dropdown data could not be loaded",
            description: `Failed: ${failedSources.join(", ")}. You can still continue with available options.`,
            status: "warning",
            duration: 3500,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Failed to load business units:", error);
        // Keep empty lists if master fetch fails
        setBusinessUnits({
          units: [],
          departments: [],
          branches: [],
          priorities: ["LOW", "MEDIUM", "HIGH", "URGENT"],
          categories: [],
        });
      }
    };
    fetchBusinessUnits();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        description: initialData.description || "",
        unit: initialData.unit || "",
        department: initialData.department || "",
        branch: initialData.branch || "",
        category: initialData.category || "",
        priority: initialData.priority || "MEDIUM",
        estimatedBudget: initialData.estimatedBudget || "",
        justification: initialData.justification || "",
        expectedDeliveryDate: formatDateForInput(
          initialData.expectedDeliveryDate,
        ),
      });
      setLineItems(normalizeLineItems(initialData.lineItems));

      const initialWorkflowId =
        initialData.workflowId?._id || initialData.workflowId || "";
      const initialWorkflowVersion =
        initialData.workflowSnapshot?.version ||
        initialData.workflowId?.version ||
        1;
      setSelectedWorkflowId(
        initialWorkflowId
          ? `${initialWorkflowId}:v${initialWorkflowVersion}`
          : "",
      );
    }
  }, [initialData]);

  // Resolve workflow when unit, department, and branch are all selected
  useEffect(() => {
    const resolveWorkflowPreview = async () => {
      if (formData.unit && formData.department && formData.branch) {
        try {
          setWorkflowLoading(true);
          const versionsResponse = await procurementApi.getWorkflowVersions(
            formData.unit,
            formData.department,
            formData.branch,
          );

          const versionsPayload = versionsResponse.data || versionsResponse;
          const payload = {
            workflows: Array.isArray(versionsPayload?.workflows)
              ? versionsPayload.workflows
              : [],
          };

          if (Array.isArray(payload?.workflows)) {
            setResolvedWorkflows(payload.workflows);

            const initialOptionKey = `${initialData?.workflowId?._id || initialData?.workflowId || ""}:v${initialData?.workflowSnapshot?.version || initialData?.workflowId?.version || 1}`;

            const nextSelectedId =
              payload.workflows.find(
                (w) =>
                  w.optionKey === selectedWorkflowId ||
                  w.optionKey === initialOptionKey,
              )?.optionKey ||
              payload.workflows[0]?.optionKey ||
              "";

            setSelectedWorkflowId(nextSelectedId);
            setResolvedWorkflow(
              payload.workflows.find((w) => w.optionKey === nextSelectedId) ||
                null,
            );
          } else {
            const singleWorkflow = payload;
            setResolvedWorkflows(singleWorkflow ? [singleWorkflow] : []);
            setResolvedWorkflow(singleWorkflow || null);
            setSelectedWorkflowId(singleWorkflow?._id || "");
          }
        } catch (error) {
          console.error("Failed to resolve workflow:", error);
          setResolvedWorkflow(null);
          setResolvedWorkflows([]);
          setSelectedWorkflowId("");

          if (error.response?.data?.error?.code !== "WORKFLOW_NOT_FOUND") {
            toast({
              title: "Workflow Resolution Error",
              description:
                error.response?.data?.error?.message ||
                "Failed to resolve workflow",
              status: "warning",
              duration: 3000,
              isClosable: true,
            });
          }
        } finally {
          setWorkflowLoading(false);
        }
      } else {
        setResolvedWorkflow(null);
        setResolvedWorkflows([]);
        setSelectedWorkflowId("");
      }
    };

    resolveWorkflowPreview();
  }, [formData.unit, formData.department, formData.branch]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  // Helper function to strip HTML tags and get plain text
  const getPlainText = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html || "";
    return tmp.textContent || tmp.innerText || "";
  };

  const validateForm = () => {
    const newErrors = {};

    // Title validation (min 5 characters)
    if (!formData.title || formData.title.trim().length < 5) {
      newErrors.title = "Title must be at least 5 characters";
    }

    // Description validation (min 10 characters) - strip HTML tags
    const plainDescription = getPlainText(formData.description);
    if (!plainDescription || plainDescription.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    // Department required
    if (!formData.department) {
      newErrors.department = "Department is required";
    }

    // Unit required
    if (!formData.unit) {
      newErrors.unit = "Unit is required";
    }

    // Branch required
    if (!formData.branch) {
      newErrors.branch = "Branch is required";
    }

    // Category required
    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    // Line items validation
    if (lineItems.length === 0) {
      newErrors.lineItems = "At least one line item is required";
      toast({
        title: "Validation Error",
        description: "Please add at least one line item",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } else {
      // Validate each line item
      const invalidItems = lineItems.filter(
        (item) =>
          !item.itemName ||
          item.itemName.trim() === "" ||
          !item.quantity ||
          item.quantity <= 0 ||
          !item.unitPrice ||
          item.unitPrice < 0,
      );
      if (invalidItems.length > 0) {
        newErrors.lineItems =
          "All line items must have name, quantity > 0, and unit price >= 0";
        toast({
          title: "Validation Error",
          description: "Please complete all line item fields",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const [workflowIdValue, workflowVersionValue] = selectedWorkflowId
        ? selectedWorkflowId.split(":v")
        : ["", ""];

      const requestData = {
        ...formData,
        lineItems: lineItems.map(({ id, ...item }) => item), // Remove temporary IDs
        workflowId: workflowIdValue || undefined,
        workflowVersion: workflowVersionValue
          ? Number(workflowVersionValue)
          : undefined,
      };

      onSubmit(requestData);
    }
  };

  const workflowOptionsToShow = resolvedWorkflows.length
    ? resolvedWorkflows
    : workflowOptions;

  const selectedWorkflow =
    workflowOptionsToShow.find((wf) => wf.optionKey === selectedWorkflowId) ||
    resolvedWorkflow;

  const approvalStages = selectedWorkflow?.stages || [];

  const getStageApproverSummary = (stage) => {
    if (!stage) {
      return "Approver details unavailable";
    }

    if (stage.approverType === "ROLE") {
      return stage.approverRole
        ? `Role based: ${stage.approverRole}`
        : "Role based approver";
    }

    const approverUsers = Array.isArray(stage.approverUsers)
      ? stage.approverUsers
      : [];

    if (approverUsers.length === 0) {
      return "User based approver (not configured)";
    }

    const approverNames = approverUsers
      .map((approver) => {
        if (!approver) {
          return "Unknown user";
        }
        return approver.name || approver.fullName || approver.email || "User";
      })
      .slice(0, 3)
      .join(", ");

    const extraCount = approverUsers.length - 3;
    return extraCount > 0
      ? `User based: ${approverNames} (+${extraCount} more)`
      : `User based: ${approverNames}`;
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        {/* Title */}
        <FormControl isInvalid={errors.title} isRequired>
          <FormLabel>Request Title</FormLabel>
          <Input
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            placeholder="e.g., Office Supplies for Q1 2026"
          />
          <FormErrorMessage>{errors.title}</FormErrorMessage>
        </FormControl>

        {/* Description */}
        <FormControl isInvalid={errors.description} isRequired>
          <FormLabel>Description</FormLabel>
          <WysiwygEditor
            value={formData.description}
            onChange={(html) => handleInputChange("description", html)}
            placeholder="Detailed description of the procurement request"
          />
          <FormErrorMessage>{errors.description}</FormErrorMessage>
        </FormControl>

        {/* Unit, Department & Branch - Auto-selection criteria */}
        <Box
          bg="blue.50"
          p={4}
          borderRadius="md"
          borderWidth="1px"
          borderColor="blue.200"
        >
          <FormLabel
            fontSize="sm"
            fontWeight="semibold"
            color="blue.700"
            mb={3}
          >
            Workflow Auto-Selection Criteria
          </FormLabel>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4}>
              <FormControl isInvalid={errors.unit} isRequired flex={1}>
                <FormLabel fontSize="sm">Unit</FormLabel>
                <Select
                  value={formData.unit}
                  onChange={(e) => handleInputChange("unit", e.target.value)}
                  placeholder="Select unit"
                  bg="white"
                >
                  {businessUnits.units.map((unit) => (
                    <option key={unit._id || unit.code} value={unit.code}>
                      {unit.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.unit}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.department} isRequired flex={1}>
                <FormLabel fontSize="sm">Department</FormLabel>
                <Select
                  value={formData.department}
                  onChange={(e) =>
                    handleInputChange("department", e.target.value)
                  }
                  placeholder="Select department"
                  bg="white"
                >
                  {businessUnits.departments.map((dept) => (
                    <option key={dept._id || dept.code} value={dept.code}>
                      {dept.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.department}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.branch} isRequired flex={1}>
                <FormLabel fontSize="sm">Branch</FormLabel>
                <Select
                  value={formData.branch}
                  onChange={(e) => handleInputChange("branch", e.target.value)}
                  placeholder="Select branch"
                  bg="white"
                >
                  {businessUnits.branches.map((branch) => (
                    <option key={branch._id || branch.code} value={branch.code}>
                      {branch.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.branch}</FormErrorMessage>
              </FormControl>
            </HStack>

            {/* Workflow Resolution Status */}
            {workflowLoading ? (
              <HStack spacing={2} pt={2}>
                <Spinner size="sm" color="blue.500" />
                <Text fontSize="sm" color="gray.600">
                  Resolving workflow...
                </Text>
              </HStack>
            ) : workflowOptionsToShow.length > 0 || showWorkflowSelect ? (
              <Box
                bg="purple.50"
                p={3}
                borderRadius="md"
                borderWidth="1px"
                borderColor="purple.200"
              >
                <VStack align="stretch" spacing={2}>
                  <HStack spacing={2}>
                    <Badge colorScheme="purple" fontSize="xs">
                      Workflow Version
                    </Badge>
                    <Text fontSize="sm" color="purple.700">
                      Select the workflow version to apply for this request
                    </Text>
                  </HStack>

                  <FormControl>
                    <Select
                      value={selectedWorkflowId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setSelectedWorkflowId(id);
                        const matched = workflowOptionsToShow.find(
                          (wf) => wf.optionKey === id,
                        );
                        setResolvedWorkflow(matched || null);
                      }}
                      bg="white"
                      placeholder="Select workflow"
                    >
                      {workflowOptionsToShow.map((wf) => {
                        const optionKey =
                          wf.optionKey ||
                          `${wf.workflowId || wf._id}:v${wf.version || 1}`;
                        return (
                          <option key={optionKey} value={optionKey}>
                            {wf.name} - v{wf.version || 1}{" "}
                            {wf.isActive ? "(Active)" : "(Inactive)"}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {selectedWorkflow?.description && (
                    <Text fontSize="xs" color="gray.600">
                      {selectedWorkflow.description}
                    </Text>
                  )}

                  {approvalStages.length > 0 && (
                    <Box
                      bg="white"
                      borderWidth="1px"
                      borderColor="purple.100"
                      borderRadius="md"
                      p={3}
                    >
                      <Text fontSize="xs" fontWeight="bold" color="purple.700">
                        Approval hierarchy preview
                      </Text>
                      <Text fontSize="xs" color="gray.600" mt={1} mb={2}>
                        Verify the approver flow before submission.
                      </Text>
                      <UnorderedList spacing={1} ml={4}>
                        {approvalStages.map((stage, index) => (
                          <ListItem key={`${stage.stageName}-${index}`}>
                            <Text as="span" fontSize="xs" fontWeight="semibold">
                              Stage {index + 1}:{" "}
                              {stage.stageName || `Stage ${index + 1}`}
                            </Text>
                            <Text as="span" fontSize="xs" color="gray.600">
                              {" - "}
                              {getStageApproverSummary(stage)}
                            </Text>
                          </ListItem>
                        ))}
                      </UnorderedList>
                    </Box>
                  )}
                </VStack>
              </Box>
            ) : resolvedWorkflow ? (
              <Box
                bg="green.50"
                p={3}
                borderRadius="md"
                borderWidth="1px"
                borderColor="green.200"
              >
                <HStack spacing={2}>
                  <Badge colorScheme="green" fontSize="xs">
                    ✓ Workflow Found
                  </Badge>
                  <Text fontSize="sm" fontWeight="semibold" color="green.700">
                    {resolvedWorkflow.name}
                  </Text>
                </HStack>
                {resolvedWorkflow.description && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    {resolvedWorkflow.description}
                  </Text>
                )}
              </Box>
            ) : formData.unit && formData.department && formData.branch ? (
              <Box
                bg="orange.50"
                p={3}
                borderRadius="md"
                borderWidth="1px"
                borderColor="orange.200"
              >
                <HStack spacing={2}>
                  <Badge colorScheme="orange" fontSize="xs">
                    ⚠ No Workflow
                  </Badge>
                  <Text fontSize="sm" color="orange.700">
                    No workflow version found for this combination
                  </Text>
                </HStack>
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Please contact administrator to create a workflow for this
                  unit/department/branch combination.
                </Text>
              </Box>
            ) : null}
          </VStack>
        </Box>

        {/* Category */}
        <HStack spacing={4}>
          <FormControl isInvalid={errors.category} isRequired flex={1}>
            <FormLabel>Category</FormLabel>
            <Select
              value={formData.category}
              onChange={(e) => handleInputChange("category", e.target.value)}
              placeholder="Select category"
            >
              {businessUnits.categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.category}</FormErrorMessage>
          </FormControl>
        </HStack>

        {/* Priority & Estimated Budget */}
        <HStack spacing={4}>
          <FormControl flex={1}>
            <FormLabel>Priority</FormLabel>
            <Select
              value={formData.priority}
              onChange={(e) => handleInputChange("priority", e.target.value)}
            >
              {businessUnits.priorities.map((priority) => (
                <option key={priority} value={priority}>
                  {priority.charAt(0) + priority.slice(1).toLowerCase()}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>Estimated Budget (₹)</FormLabel>
            <Input
              type="number"
              value={formData.estimatedBudget}
              onChange={(e) =>
                handleInputChange("estimatedBudget", e.target.value)
              }
              placeholder="Enter estimated budget"
            />
          </FormControl>
        </HStack>

        {/* Expected Delivery Date */}
        <FormControl>
          <FormLabel>Expected Delivery Date</FormLabel>
          <Input
            type="date"
            value={formData.expectedDeliveryDate}
            onChange={(e) =>
              handleInputChange("expectedDeliveryDate", e.target.value)
            }
          />
        </FormControl>

        {/* Justification */}
        <FormControl>
          <FormLabel>Justification</FormLabel>
          <WysiwygEditor
            value={formData.justification}
            onChange={(html) => handleInputChange("justification", html)}
            placeholder="Business justification for this procurement. You can paste Excel/Word tables directly."
          />
        </FormControl>

        {/* Line Items */}
        <FormControl isInvalid={errors.lineItems} isRequired>
          <FormLabel>Line Items</FormLabel>
          <LineItemsTable lineItems={lineItems} setLineItems={setLineItems} />
          <FormErrorMessage>{errors.lineItems}</FormErrorMessage>
        </FormControl>

        {/* Master data empty guard */}
        {(businessUnits.units.length === 0 ||
          businessUnits.departments.length === 0 ||
          businessUnits.branches.length === 0 ||
          businessUnits.categories.length === 0) && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              Some master data is missing. Please add{" "}
              {[
                businessUnits.units.length === 0 && "Units",
                businessUnits.departments.length === 0 && "Departments",
                businessUnits.branches.length === 0 && "Branches",
                businessUnits.categories.length === 0 && "Categories",
              ]
                .filter(Boolean)
                .join(", ")}{" "}
              in <strong>Organizational Structure Maintenance</strong> first.
            </Text>
          </Alert>
        )}

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={onCancel} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isLoading}
            isDisabled={
              businessUnits.units.length === 0 ||
              businessUnits.departments.length === 0 ||
              businessUnits.branches.length === 0 ||
              businessUnits.categories.length === 0
            }
            title={
              businessUnits.units.length === 0 ||
              businessUnits.departments.length === 0 ||
              businessUnits.branches.length === 0 ||
              businessUnits.categories.length === 0
                ? "Please populate master data before submitting"
                : undefined
            }
          >
            {initialData ? "Update Request" : "Create Request"}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default RequestForm;
